import type { ConversationMessage } from '@deskworks/shared-types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '@/lib/api';
import { locale, t } from '@/lib/i18n';
import { useRealtimeEnvelope } from '@/lib/realtime';

/** Where a voice session stands: VICKI listens, speaks, or works with a tool. */
export type LiveState = 'idle' | 'connecting' | 'listening' | 'hearing' | 'speaking' | 'working' | 'error';

/** One line of what was said: by the person, by VICKI, or a tool she uses. */
export interface LiveLine {
  id: string;
  who: 'you' | 'vicki' | 'tool';
  text: string;
}

type Send = (event: Record<string, unknown>) => void;

/** How a tool shows while it runs. */
function toolLine(name: string, args: Record<string, any>) {
  if (name === 'write_to_chat') return t('Writes into the chat');
  if (name === 'chat_status') return t('Looks how the chat stands');
  if (name === 'list_account_tools') return t('Looks for a tool: {query}', { query: args.query ?? '…' });
  if (name === 'call_account_tool') return String(args.tool ?? '').replaceAll('_', ' ');
  if (name === 'delegate_work') return t('Hands to a desk: {title}', { title: args.title ?? '' });
  if (name === 'decide_card') return args.decision === 'approve' ? t('Approves the card') : t('Rejects the card');
  if (name === 'recall') return t('Remembers: {query}', { query: args.query ?? '' });
  return name;
}

/** What a new message of the chat tells the voice. */
function newsOf(msg: ConversationMessage) {
  if (msg.senderType === 'agent') return `Your worker wrote in the chat:\n${msg.content}`;
  if (msg.senderType === 'user') return `${msg.senderName ?? 'Someone'} wrote in the chat:\n${msg.content}`;
  return `Notice in the chat: ${msg.content}`;
}

/**
 * Live VICKI (docs/features/front-desk.md#live-vicki): a voice conversation with VICKI in a chat over WebRTC with OpenAI's
 * realtime model. The Control Plane mints a short-lived key and runs her tools; new messages of the chat (her worker's
 * answers, results of desks, cards) reach the voice, which tells the person.
 */
export function useLiveVicki(conversationId: string) {
  const [state, setState] = useState<LiveState>('idle');
  const [lines, setLines] = useState<LiveLine[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [streams, setStreams] = useState<{ mic: MediaStream | null; voice: MediaStream | null }>({ mic: null, voice: null });

  const session = useRef<{ pc: RTCPeerConnection; dc: RTCDataChannel; mic: MediaStream; audio: HTMLAudioElement } | null>(null);
  const flags = useRef({ responding: false, speaking: false, hearing: false, calls: 0 });
  /** News of the chat waiting for a pause; `speak`: VICKI should say something about it. */
  const queue = useRef<{ text: string; speak: boolean }[]>([]);
  /** What write_to_chat sends, so the session doesn't hear its own message as news. */
  const written = useRef(new Set<string>());

  const line = useCallback((id: string, who: LiveLine['who'], text: string, append = false) => {
    setLines((old) => {
      const i = old.findIndex((l) => l.id === id);
      if (i < 0) return [...old.slice(-30), { id, who, text }];
      const next = [...old];
      next[i] = { ...next[i], text: append ? next[i].text + text : text };
      return next;
    });
  }, []);

  const send: Send = useCallback((event) => {
    const dc = session.current?.dc;
    if (dc?.readyState === 'open') dc.send(JSON.stringify(event));
  }, []);

  const settle = useCallback(() => {
    const f = flags.current;
    if (!session.current) return;
    setState(f.calls ? 'working' : f.speaking || f.responding ? 'speaking' : f.hearing ? 'hearing' : 'listening');
  }, []);

  /** The chat's news once VICKI and the person pause; she speaks about it when any of it asks for that. */
  const flush = useCallback(() => {
    const f = flags.current;
    if (!queue.current.length || f.responding || f.hearing || f.calls) return;
    const items = queue.current.splice(0);
    for (const n of items) send({ type: 'conversation.item.create', item: { type: 'message', role: 'system', content: [{ type: 'input_text', text: n.text }] } });
    if (items.some((n) => n.speak)) {
      f.responding = true;
      send({ type: 'response.create' });
    }
  }, [send]);

  /** Her tool calls of one response, through the Control Plane; then she goes on. */
  const runCalls = useCallback(
    async (calls: { call_id: string; name: string; arguments: string }[]) => {
      const f = flags.current;
      f.calls += calls.length;
      settle();
      await Promise.all(
        calls.map(async (c) => {
          let args: Record<string, any> = {};
          try {
            args = JSON.parse(c.arguments || '{}');
          } catch {
            /* the model's arguments were not JSON: the tool says what it misses */
          }
          line(c.call_id, 'tool', toolLine(c.name, args));
          if (c.name === 'write_to_chat' && args.message) written.current.add(String(args.message).trim());
          let output: string;
          try {
            const r = await api.frontDesk.liveCall(conversationId, c.name, args);
            output = r.isError ? `Error: ${r.output}` : r.output;
          } catch (err) {
            output = `Error: ${errorMessage(err)}`;
          }
          send({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: c.call_id, output } });
        }),
      );
      f.calls -= calls.length;
      if (!session.current) return;
      f.responding = true;
      send({ type: 'response.create' });
      settle();
    },
    [conversationId, line, send, settle],
  );

  const onEvent = useCallback(
    (e: any) => {
      const f = flags.current;
      switch (e.type) {
        case 'input_audio_buffer.speech_started':
          f.hearing = true;
          break;
        case 'input_audio_buffer.speech_stopped':
          f.hearing = false;
          break;
        case 'conversation.item.input_audio_transcription.delta':
          line(`you-${e.item_id}`, 'you', e.delta ?? '', true);
          break;
        case 'conversation.item.input_audio_transcription.completed':
          if (e.transcript?.trim()) line(`you-${e.item_id}`, 'you', e.transcript.trim());
          break;
        case 'response.created':
          f.responding = true;
          break;
        case 'response.output_audio_transcript.delta':
        case 'response.audio_transcript.delta':
          line(`vicki-${e.item_id}`, 'vicki', e.delta ?? '', true);
          break;
        case 'output_audio_buffer.started':
          f.speaking = true;
          break;
        case 'output_audio_buffer.stopped':
        case 'output_audio_buffer.cleared':
          f.speaking = false;
          break;
        case 'response.done': {
          f.responding = false;
          const calls = (e.response?.output ?? []).filter((o: any) => o.type === 'function_call');
          if (calls.length) void runCalls(calls);
          break;
        }
        case 'error':
          // A response asked for while one runs is refused; everything else she says out loud.
          if (e.error?.code !== 'conversation_already_has_active_response') setError(e.error?.message ?? t('The voice connection had a problem.'));
          break;
      }
      settle();
      flush();
    },
    [flush, line, runCalls, settle],
  );

  const stop = useCallback(() => {
    const s = session.current;
    session.current = null;
    if (s) {
      s.dc.close();
      s.pc.close();
      s.mic.getTracks().forEach((track) => track.stop());
      s.audio.srcObject = null;
    }
    flags.current = { responding: false, speaking: false, hearing: false, calls: 0 };
    queue.current = [];
    setStreams({ mic: null, voice: null });
    setMuted(false);
    setState('idle');
  }, []);

  const start = useCallback(async () => {
    if (session.current) return;
    setError(null);
    setLines([]);
    setState('connecting');
    let mic: MediaStream | null = null;
    let pc: RTCPeerConnection | null = null;
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
      const live = await api.frontDesk.startLive(conversationId, locale);
      pc = new RTCPeerConnection();
      const audio = new Audio();
      audio.autoplay = true;
      pc.ontrack = (ev) => {
        audio.srcObject = ev.streams[0];
        setStreams((s) => ({ ...s, voice: ev.streams[0] }));
      };
      mic.getTracks().forEach((track) => pc!.addTrack(track, mic!));
      const dc = pc.createDataChannel('oai-events');
      dc.onmessage = (m) => {
        try {
          onEvent(JSON.parse(m.data));
        } catch {
          /* not an event */
        }
      };
      dc.onopen = () => {
        // She opens the conversation, with what waits in the chat if anything does.
        flags.current.responding = true;
        send({
          type: 'response.create',
          response: { instructions: 'Greet them by first name in one short sentence. If something in the chat waits for them or is still running, say so in a second sentence.' },
        });
        settle();
      };
      pc.onconnectionstatechange = () => {
        if (pc && ['failed', 'disconnected'].includes(pc.connectionState) && session.current?.pc === pc) {
          setError(t('The voice connection broke off.'));
          stop();
        }
      };
      session.current = { pc, dc, mic, audio };
      setStreams({ mic, voice: null });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const res = await fetch(live.callUrl, {
        method: 'POST',
        body: offer.sdp,
        headers: { authorization: `Bearer ${live.clientSecret}`, 'content-type': 'application/sdp' },
      });
      if (!res.ok) {
        // OpenAI says why (e.g. 429 insufficient_quota: no credits left on the runtime's organization).
        const reason = ((await res.json().catch(() => null)) as { error?: { message?: string } } | null)?.error?.message;
        throw new Error(reason ? t('OpenAI did not take the call: {reason}', { reason }) : t('OpenAI did not take the call (HTTP {status}).', { status: res.status }));
      }
      await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
    } catch (err) {
      if (session.current) stop();
      else {
        mic?.getTracks().forEach((track) => track.stop());
        pc?.close();
      }
      const name = (err as { name?: string }).name;
      setError(name === 'NotAllowedError' ? t('The microphone is blocked: allow it for this site in the browser.') : errorMessage(err));
      setState('error');
    }
  }, [conversationId, onEvent, send, settle, stop]);

  const toggleMute = useCallback(() => {
    const mic = session.current?.mic;
    if (!mic) return;
    setMuted((m) => {
      mic.getAudioTracks().forEach((track) => (track.enabled = m));
      return !m;
    });
  }, []);

  // The chat's new messages reach the voice: her worker's answers, results of desks, cards, other people.
  useRealtimeEnvelope((env) => {
    if (!session.current || env.event !== 'conversation.message') return;
    const msg = env.data as ConversationMessage;
    if (msg?.conversationId !== conversationId || typeof msg.content !== 'string') return;
    if (msg.senderType === 'user' && written.current.delete(msg.content.trim())) return;
    // What comes while one of her own calls runs (a card, a hand-off) she learns from its result already.
    const speak = !flags.current.calls && (msg.metadata as { event?: string } | undefined)?.event !== 'created';
    queue.current.push({ text: newsOf(msg), speak });
    flush();
  });

  useEffect(() => stop, [conversationId, stop]);

  return { state, lines, error, muted, streams, start, stop, toggleMute, active: state !== 'idle' && state !== 'error' };
}
